import { motion } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';
import { SOVEREIGN } from '../../../epc/utils/epcProjectVisual';

interface Props {
  icon: LucideIcon;
  title: string;
  sub: string;
  step: number;
  totalSteps: number;
  children: React.ReactNode;
  /** Optional: highlight when this is the section currently in focus during loop / scroll-tracking. */
  isFocused?: boolean;
}

/**
 * Chrome for the active section in the Edit Role view.
 *
 * Visual:
 *   - Cream/white surface with very soft shadow
 *   - Sorigin-green icon tile (gradient primary → accent)
 *   - Title + helper sub + step counter
 *   - Children render below a hairline divider
 *
 * Motion: framer-motion entry — opacity + y rise, 0.4s.
 * When `isFocused`, a subtle ring pulses around the card (camera-zoom feel).
 */
export default function SectionCard({
  icon: Icon,
  title,
  sub,
  step,
  totalSteps,
  children,
  isFocused = false,
}: Props) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 16, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className="relative rounded-2xl overflow-hidden"
      style={{
        border: `1px solid ${SOVEREIGN.cardBorder}`,
        background: SOVEREIGN.cardBg,
        boxShadow: isFocused
          ? `0 0 0 4px ${SOVEREIGN.brand}1A, ${SOVEREIGN.cardShadow}`
          : SOVEREIGN.cardShadow,
        transition: 'box-shadow 400ms ease',
      }}
    >
      <header
        className="flex items-center gap-3 px-5 py-4"
        style={{ borderBottom: `1px solid ${SOVEREIGN.innerRule}` }}
      >
        <motion.div
          whileHover={{ scale: 1.05, rotate: 2 }}
          transition={{ type: 'spring', stiffness: 400, damping: 17 }}
          className="flex h-10 w-10 items-center justify-center rounded-xl text-white shadow-sm shrink-0"
          style={{ background: SOVEREIGN.bottle }}
        >
          <Icon size={18} strokeWidth={2.2} />
        </motion.div>
        <div className="flex-1 min-w-0">
          <h3
            className="text-[15px] font-semibold leading-tight"
            style={{ color: SOVEREIGN.title }}
          >
            {title}
          </h3>
          <p
            className="text-[12px] mt-0.5 leading-snug"
            style={{ color: SOVEREIGN.kpiCaption }}
          >
            {sub}
          </p>
        </div>
        <span
          className="text-[11px] font-medium tracking-wide whitespace-nowrap"
          style={{ color: SOVEREIGN.kpiCaption }}
        >
          Step {step} of {totalSteps}
        </span>
      </header>

      <div className="px-5 py-5">{children}</div>
    </motion.section>
  );
}
